/* =====================================================================
   The 12 Limits-to-Growth scenarios
   ---------------------------------------------------------------------
   1. SCENARIOS — narrative layer: names, 2026 probability reads,
      mechanisms, failure modes. Sources: The Limits to Growth (1972),
      the 30-Year Update (2004), Turner (2008/2014), Herrington (2021),
      Iceberg research corpus.
   2. W3_PARAMS — parameter layer: for each scenario, the World3 (1972)
      policy switches that reproduce the corresponding model run, as
      documented in "Dynamics of Growth in a Finite World" (1974) ch. 7.
   3. Scenarios — cached World3 runs + chart projection + the validation
      suite (37 behavior-mode checks, ported from the prototype).
   ===================================================================== */
import { World3, type W3Params, type W3Series } from './world3.js';

export type ScenarioId =
  | 'bau1' | 'bau2' | 'unlim' | 'pollctrl' | 'crop' | 'birth' | 'ct'
  | 'popstab' | 'capstab' | 'popcap' | 'sw' | 'delayed';

export interface ScenarioNarrative {
  id: ScenarioId;
  code: string;
  name: string;
  full: string;
  /** 2026 probability read, 0..100 (0 = temporally obsolete, not modeled) */
  prob: number;
  des: string;
  cat: 'BAU' | 'Tech' | 'Stabilization';
  limit: string;
  arch: string;
  peak: string;
  tagline: string;
  mechanism: string;
  failure: string;
  outcome: string;
  rationale: string;
}

export const SCENARIOS: ScenarioNarrative[] = [
  { id:'bau1', code:'BAU1', name:'Standard Run', full:'Run 1 — Business as Usual', prob:75, des:'neutral', cat:'BAU', limit:'resource', arch:'collapse-fast', peak:'~2015–2030',
    tagline:'History continues unchanged.',
    mechanism:'Exponential industrial and population growth depletes non-renewable resources. As reserves fall, the fraction of capital allocated to extraction (fcaor) rises non-linearly, diverting investment away from agriculture, health and services.',
    failure:'Industrial output and food per capita peak, pollution keeps climbing, and mortality rises as medical and food systems are starved of capital.',
    outcome:'Overshoot and collapse in the first half of the 21st century.',
    rationale:'Moderate–High. A near-perfect historical fit, but unconventional extraction (fracking, deep-water drilling) has masked conventional depletion — nudging the real world toward BAU2.' },
  { id:'bau2', code:'BAU2', name:'Double Resources', full:'Run 2 — Business as Usual 2', prob:90, des:'undesirable', cat:'BAU', limit:'pollution', arch:'collapse-sharp', peak:'~2020–2040',
    tagline:'Twice the resources, worse the fall.',
    mechanism:'Doubled reserves plus recycling relax the resource limit; growth runs ~20 years longer, generating a massive surge of persistent, lagging pollution.',
    failure:'Accumulated toxic load degrades soils and human health; agriculture collapses in a steeper, more destructive overshoot than the Standard Run.',
    outcome:'Pollution-driven overshoot & collapse; welfare peaks ~2020–2030, population ~2040.',
    rationale:'Very High. One of the two closest-fitting scenarios to real-world data — it correlates directly with the modern climate crisis and biosphere degradation.' },
  { id:'unlim', code:'UNLIM', name:'Unlimited Resources', full:'Run 3 — Unlimited Nuclear Energy + Recycling', prob:50, des:'neutral', cat:'Tech', limit:'pollution', arch:'collapse-pollution', peak:'mid-century',
    tagline:'Cheap energy, saturated sinks.',
    mechanism:'"Unlimited" nuclear energy and 100% recycling remove the resource limit; industrial output surges with no pollution controls.',
    failure:'The biosphere\'s assimilation sinks saturate; a lethal, lagging pollution crisis collapses agriculture and life expectancy.',
    outcome:'Sharp pollution-driven decline.',
    rationale:'Moderate. Mirrors real energy diversification without material or carbon ceilings, leading to ecological over-saturation.' },
  { id:'pollctrl', code:'POLL', name:'Pollution Controls', full:'Run 4 — Unlimited Energy + Pollution Controls', prob:70, des:'neutral', cat:'Tech', limit:'land', arch:'overshoot-land', peak:'~2050s',
    tagline:'Clean growth hits the land wall.',
    mechanism:'Emissions per unit of output are cut ~75%. With resource and pollution limits bypassed, population and capital expand deep into the century.',
    failure:'Arable land is exhausted by urban and agricultural demand; intensive yields peak and collapse via soil erosion and encroachment.',
    outcome:'Land-driven food crisis and population decline.',
    rationale:'Moderate–High. The land-system-change boundary is already stressed as urban expansion consumes prime soils.' },
  { id:'crop', code:'CROP', name:'Crop Yields', full:'Run 5 — …+ Increased Crop Yields', prob:70, des:'neutral', cat:'Tech', limit:'agri-pollution', arch:'overshoot-agri', peak:'~2050s',
    tagline:'Yields doubled, cycles broken.',
    mechanism:'Crop yields are artificially doubled to bypass the land limit; intensive chemical inputs create a concentrated wave of agricultural pollution.',
    failure:'Runoff and residual pollution poison freshwater and soils; soil structure collapses and food per capita crashes.',
    outcome:'Agricultural collapse and population crash.',
    rationale:'Moderate–High. Matches the disruption of nitrogen/phosphorus cycles, soil toxicity and biodiversity decline.' },
  { id:'birth', code:'BIRTH', name:'Birth Control', full:'Run 6 — …+ Perfect Birth Control', prob:60, des:'neutral', cat:'Tech', limit:'capital', arch:'plateau-late', peak:'~2050–2070',
    tagline:'Stable population, restless capital.',
    mechanism:'Universal family planning stabilizes population, but the drive for continuous growth keeps industrial capital accumulating exponentially.',
    failure:'The sheer absolute scale of industry overwhelms pollution controls, triggering a delayed ecological collapse and rising mortality.',
    outcome:'Late-century delayed pollution collapse.',
    rationale:'Moderate. Fertility is falling, but per-capita ecological footprint keeps rising — stabilizing population alone is not enough.' },
  { id:'ct', code:'CT', name:'Comprehensive Technology', full:'Run 7 — Comprehensive Technology', prob:85, des:'neutral', cat:'Tech', limit:'tech-cost', arch:'soft-landing', peak:'~2030–2050 plateau',
    tagline:'The technologist\'s soft landing.',
    mechanism:'All fixes active at once — energy, pollution control, doubled yields, family planning. Outright catastrophic collapse is avoided.',
    failure:'Maintenance costs of the technology stack escalate non-linearly, starving reinvestment — producing a gradual "soft landing" decline.',
    outcome:'Growth halts; slow decline in output, food and welfare, without a sudden crash.',
    rationale:'Very High. Shares best-fit status with BAU2 — governments and corporations are actively betting on carbon removal, geoengineering and GM yields to keep growing.' },
  { id:'popstab', code:'POP', name:'Population Stabilized', full:'Run 8 — Stabilized Population (1975)', prob:0, des:'neutral', cat:'Stabilization', limit:'obsolete', arch:'obsolete', peak:'—',
    tagline:'A road not taken (1975).',
    mechanism:'Population stabilized in 1975, but unconstrained industrial capital continues drawing down resources.',
    failure:'Resource scarcity triggers the Standard-Run capital-diversion crisis.',
    outcome:'Obsolete — population doubled instead of stabilizing.',
    rationale:'Near Zero. Temporally obsolete; the 1975 window has closed.' },
  { id:'capstab', code:'CAP', name:'Capital Stabilized', full:'Run 9 — Stabilized Capital (1975)', prob:0, des:'neutral', cat:'Stabilization', limit:'obsolete', arch:'obsolete', peak:'—',
    tagline:'A road not taken (1975).',
    mechanism:'Capital reinvestment capped in 1975, but population keeps growing and outstrips the fixed food-production capacity.',
    failure:'Structural famine and population collapse.',
    outcome:'Obsolete — globalization accelerated capital instead.',
    rationale:'Near Zero. Temporally obsolete; capital was never capped in 1975.' },
  { id:'popcap', code:'POP+CAP', name:'Pop + Capital Stabilized', full:'Run 10 — Stabilized World (1975)', prob:0, des:'neutral', cat:'Stabilization', limit:'obsolete', arch:'obsolete', peak:'—',
    tagline:'The missed equilibrium.',
    mechanism:'Population and capital both stabilized in 1975 — a true steady-state economy within carrying capacity.',
    failure:'None in-model — but it required acting in the 1970s.',
    outcome:'Would have been a high, stable standard of living.',
    rationale:'Zero. The clean 1975 equilibrium is physically impossible to reach retroactively.' },
  { id:'sw', code:'SW', name:'Stabilized World', full:'Run 11 — Stabilized World (post-2002)', prob:15, des:'desirable', cat:'Stabilization', limit:'none', arch:'sustainable-rise', peak:'rising to 2100',
    tagline:'Deliberate equilibrium.',
    mechanism:'A post-2002 value shift: caps on output, priority to health & education, recycling, soil restoration and birth control bring the footprint below carrying capacity.',
    failure:'None — collapse is avoided entirely.',
    outcome:'High, sustainable welfare (~8bn) through the end of the century.',
    rationale:'Low. This path diverged most from real data — the smooth-transition window is closing; it now needs an Earth4All "Giant Leap".' },
  { id:'delayed', code:'DELAY', name:'Delayed Stabilization', full:'Run 12 — Delayed Stabilization (2000+)', prob:30, des:'desirable', cat:'Stabilization', limit:'multi-boundary', arch:'degraded-eq', peak:'dip then recover',
    tagline:'Survive first, stabilize later.',
    mechanism:'Stabilization policies are delayed to 2000+. The system overshoots, then is forced into a painful contraction before stabilizing at a lower baseline.',
    failure:'A temporary collapse / welfare dip precedes a degraded equilibrium.',
    outcome:'A stabilized world, but at a significantly lower quality of life.',
    rationale:'Represents today\'s active regenerative-policy paradigm — any stabilization in 2026 starts from a degraded baseline. (Research rates this as high as 80%.)' },
];

export const SCN_BY_ID: Record<ScenarioId, ScenarioNarrative> =
  Object.fromEntries(SCENARIOS.map(s => [s.id, s])) as Record<ScenarioId, ScenarioNarrative>;

/* ---------- World3 parameterizations of the 12 runs ---------- */

/** Soil-preservation policy: erosion at high yield is halved. */
export const W3_SOIL_PRESERVATION: number[] =
  [1.2, 1, 0.63, 0.36, 0.16, 0.055, 0.04, 0.025, 0.015, 0.01].map(v => Math.min(1.2, v * 2));

/** The full stabilization package of the Stabilized World runs. */
function stabilization(pyear: number, capDelay: number): W3Params {
  return {
    policyYear: pyear,
    popEqYear: pyear + 10,
    zpgYear: pyear,
    fceMaxYear: pyear,
    indEqYear: pyear + capDelay,
    nrufAfter: 0.25,
    ppgfAfter: 0.25,
    alicAfter: 21,
    llmyAfterData: W3_SOIL_PRESERVATION,
  };
}

export const W3_PARAMS: Record<ScenarioId, W3Params> = {
  // Run 1 — Business as usual: the untouched standard run.
  bau1:     {},
  // Run 2 — Double resources: reserves x2, nothing else changes.
  bau2:     { nri: 2e12 },
  // Run 3 — "Unlimited" resources: reserves x2 AND extraction cost held low from 1975.
  unlim:    { nri: 2e12, policyYear: 1975, fcaorAfterConst: 0.05 },
  // Run 4 — ... + pollution control: emissions per unit output -75%.
  pollctrl: { nri: 2e12, policyYear: 1975, fcaorAfterConst: 0.05, ppgfAfter: 0.25 },
  // Run 5 — ... + doubled land yields.
  crop:     { nri: 2e12, policyYear: 1975, fcaorAfterConst: 0.05, ppgfAfter: 0.25, lyfAfter: 2 },
  // Run 6 — ... + perfect birth control.
  birth:    { nri: 2e12, policyYear: 1975, fcaorAfterConst: 0.05, ppgfAfter: 0.25, lyfAfter: 2,
              fceMaxYear: 1975 },
  // Run 7 — Comprehensive technology (incl. soil conservation): the soft landing.
  ct:       { nri: 2e12, policyYear: 1975, fcaorAfterConst: 0.05, nrufAfter: 0.25, ppgfAfter: 0.25,
              lyfAfter: 2, fceMaxYear: 1975, llmyAfterData: W3_SOIL_PRESERVATION },
  // Run 8 — Population stabilized in 1975; capital keeps growing.
  popstab:  { policyYear: 1975, popEqYear: 1975 },
  // Run 9 — Capital stabilized in 1975; population keeps growing.
  capstab:  { policyYear: 1975, indEqYear: 1975 },
  // Run 10 — The missed 1975 equilibrium: full stabilization package.
  popcap:   { ...stabilization(1975, 15), popEqYear: 1975 },
  // Run 11 — Stabilized World, post-2002 value shift.
  sw:       { ...stabilization(2002, 20), popEqYear: 2012 },
  // Run 12 — Delayed stabilization: the world acts in the early 2030s.
  delayed:  stabilization(2032, 10),
};

/* =====================================================================
   Scenarios — cached runs, chart projection, checkpoints, validation
   ===================================================================== */

const _cache = new Map<ScenarioId, W3Series>();

/** Run (once) and cache the World3 trajectories for a scenario. */
export function series(id: ScenarioId): W3Series {
  let s = _cache.get(id);
  if (!s) {
    const params = W3_PARAMS[id];
    if (!params) throw new Error(`Scenarios.series: unknown scenario "${id}"`);
    s = World3.run(params);
    _cache.set(id, s);
  }
  return s;
}

export function runAll(): void { SCENARIOS.forEach(s => series(s.id)); }

/* Display projection for the 1900–2100 variables chart. pop scales linearly
   against a fixed 16e9 reference; io / food / ppolx are soft-compressed
   (x / (x + h)) so collapse worlds and hyper-industrial tech worlds are both
   legible on one 0..1 axis; nrfr is already a 0..1 fraction. Half-saturation
   constants chosen against cross-scenario maxima (io 15.4e12, food 9.4e12,
   ppolx 70). */
export interface ChartPoint { x: number; pop: number; ind: number; food: number; poll: number; res: number; }

export function chartSeries(id: ScenarioId): ChartPoint[] {
  const ser = series(id);
  const out: ChartPoint[] = [];
  const clamp01 = (v: number) => Math.max(0, Math.min(1, v));
  for (let i = 0; i < ser.years.length; i++) {
    out.push({
      x: (ser.years[i] - 1900) / 200,
      pop:  clamp01(ser.pop[i] / 16e9),
      ind:  clamp01(ser.io[i] / (ser.io[i] + 4e12)),
      food: clamp01(ser.food[i] / (ser.food[i] + 2.5e12)),
      poll: clamp01(ser.ppolx[i] / (ser.ppolx[i] + 8)),
      res:  clamp01(ser.nrfr[i]),
    });
  }
  return out;
}

/* ---------- helpers ---------- */
type NumKey = { [K in keyof W3Series]: W3Series[K] extends number[] ? K : never }[keyof W3Series];

export function at(ser: W3Series, k: NumKey, yr: number): number {
  return ser[k][ser.years.indexOf(yr)];
}
export function peak(ser: W3Series, k: NumKey, fromYr = 1900, toYr = 2100): { year: number; val: number } {
  let m = -Infinity, yi = 0;
  ser[k].forEach((v, i) => {
    const y = ser.years[i];
    if (y >= fromYr && y <= toYr && v > m) { m = v; yi = i; }
  });
  return { year: ser.years[yi], val: m };
}
export function trough(ser: W3Series, k: NumKey, fromYr = 1900, toYr = 2100): { year: number; val: number } {
  let m = Infinity, yi = 0;
  ser[k].forEach((v, i) => {
    const y = ser.years[i];
    if (y >= fromYr && y <= toYr && v < m) { m = v; yi = i; }
  });
  return { year: ser.years[yi], val: m };
}

export interface ScenarioCheckpoints {
  id: ScenarioId;
  popPeak: { year: number; val: number }; pop2100: number;
  iopcPeak: { year: number; val: number }; iopc2100: number;
  fpcPeak: { year: number; val: number }; fpc2100: number;
  ppolxPeak: { year: number; val: number };
  le2100: number; nrfr2100: number;
}

export function checkpoints(id: ScenarioId): ScenarioCheckpoints {
  const s = series(id);
  return {
    id,
    popPeak: peak(s, 'pop'), pop2100: at(s, 'pop', 2100),
    iopcPeak: peak(s, 'iopc'), iopc2100: at(s, 'iopc', 2100),
    fpcPeak: peak(s, 'fpc'), fpc2100: at(s, 'fpc', 2100),
    ppolxPeak: peak(s, 'ppolx'),
    le2100: at(s, 'le', 2100), nrfr2100: at(s, 'nrfr', 2100),
  };
}

/* ---------- validation suite ----------
   Behavior-mode checks against the published runs (LtG 1972 figs. 35–48,
   the 30-Year Update, the Iceberg research corpus). Wide tolerances by
   design: we assert peak windows, collapse depths and equilibria — not
   exact numbers. */
export interface Check { id: ScenarioId | 'suggest'; name: string; pass: boolean; detail: string; }
export interface ValidationResult { checks: Check[]; passed: number; failed: number; }

export function validate(): ValidationResult {
  const R = {} as Record<ScenarioId, W3Series>;
  SCENARIOS.forEach(s => { R[s.id] = series(s.id); });
  const checks: Check[] = [];
  const near = (v: number, lo: number, hi: number) => v >= lo && v <= hi;
  const check = (id: ScenarioId, name: string, pass: boolean, detail: string) =>
    checks.push({ id, name, pass, detail });

  { // bau1 — overshoot & collapse, first half of 21st century
    const s = R.bau1, pp = peak(s, 'pop'), ip = peak(s, 'iopc'), fp = peak(s, 'fpc'), xp = peak(s, 'ppolx');
    check('bau1', 'population peaks 2020–2050 at 7–11bn', near(pp.year, 2020, 2050) && near(pp.val, 7e9, 11e9), `${pp.year}, ${(pp.val / 1e9).toFixed(1)}bn`);
    check('bau1', 'industrial output per capita peaks 2005–2035', near(ip.year, 2005, 2035), String(ip.year));
    check('bau1', 'food per capita peaks 1995–2025', near(fp.year, 1995, 2025), String(fp.year));
    check('bau1', 'pollution peaks at 6–16x 1970 level', near(xp.val, 6, 16), `${xp.val.toFixed(1)}x in ${xp.year}`);
    check('bau1', 'collapse: population falls ≥30% from peak by 2100', at(s, 'pop', 2100) <= 0.7 * pp.val, `${(100 * at(s, 'pop', 2100) / pp.val).toFixed(0)}% of peak`);
  }
  { // bau2 — later, pollution-driven, harder
    const a = R.bau1, b = R.bau2;
    const ia = peak(a, 'iopc'), ib = peak(b, 'iopc'), xa = peak(a, 'ppolx'), xb = peak(b, 'ppolx');
    check('bau2', 'growth runs 10–30 years longer than BAU1', near(ib.year - ia.year, 10, 30), `+${ib.year - ia.year} yrs`);
    check('bau2', 'pollution surge ≥2x the BAU1 peak', xb.val >= 2 * xa.val, `${xb.val.toFixed(1)}x vs ${xa.val.toFixed(1)}x`);
    const da = at(a, 'pop', 2100) / peak(a, 'pop').val, db = at(b, 'pop', 2100) / peak(b, 'pop').val;
    check('bau2', 'collapse deeper than BAU1', db < da, `${(100 * db).toFixed(0)}% vs ${(100 * da).toFixed(0)}% of peak`);
  }
  { // unlim — pollution crisis unbound
    const s = R.unlim, xp = peak(s, 'ppolx'), fp = peak(s, 'fpc');
    check('unlim', 'worst pollution of all BAU/tech worlds', xp.val > peak(R.bau2, 'ppolx').val, `${xp.val.toFixed(1)}x`);
    check('unlim', 'food collapses below 60% of its peak', at(s, 'fpc', 2100) < 0.6 * fp.val, `${(100 * at(s, 'fpc', 2100) / fp.val).toFixed(0)}%`);
  }
  { // pollctrl — clean growth hits the land wall
    const s = R.pollctrl, fp = peak(s, 'fpc'), xp = peak(s, 'ppolx');
    check('pollctrl', 'pollution controlled (≤1/3 of unlim peak)', xp.val <= peak(R.unlim, 'ppolx').val / 3, `${xp.val.toFixed(1)}x`);
    check('pollctrl', 'food per capita peaks 2030–2060 (land limit)', near(fp.year, 2030, 2060), String(fp.year));
    check('pollctrl', 'food declines ≥35% from peak by 2100', at(s, 'fpc', 2100) <= 0.65 * fp.val, `${(100 * at(s, 'fpc', 2100) / fp.val).toFixed(0)}%`);
  }
  { // crop — agricultural collapse
    const s = R.crop, pp = peak(s, 'pop');
    check('crop', 'population peaks 2045–2080', near(pp.year, 2045, 2080), String(pp.year));
    check('crop', 'population falls ≥30% from peak (ag collapse)', at(s, 'pop', 2100) <= 0.7 * pp.val, `${(100 * at(s, 'pop', 2100) / pp.val).toFixed(0)}%`);
    check('crop', 'end-century famine: food/capita below subsistence (230)', at(s, 'fpc', 2100) < 230, `${at(s, 'fpc', 2100).toFixed(0)} kg`);
  }
  { // birth — stabilized population, restless capital: softer than crop
    const s = R.birth, pp = peak(s, 'pop');
    check('birth', 'population peaks 2045–2075', near(pp.year, 2045, 2075), String(pp.year));
    check('birth', 'late-century decline milder than CROP (higher end welfare)', at(s, 'iopc', 2100) > at(R.crop, 'iopc', 2100), `${at(s, 'iopc', 2100).toFixed(0)} vs ${at(R.crop, 'iopc', 2100).toFixed(0)}`);
  }
  { // ct — the technologist's soft landing
    const s = R.ct, pp = peak(s, 'pop'), ip = peak(s, 'iopc');
    check('ct', 'no sudden crash: population ≥55% of peak at 2100', at(s, 'pop', 2100) >= 0.55 * pp.val, `${(100 * at(s, 'pop', 2100) / pp.val).toFixed(0)}%`);
    check('ct', 'industrial output per capita holds ≥60% of peak', at(s, 'iopc', 2100) >= 0.6 * ip.val, `${(100 * at(s, 'iopc', 2100) / ip.val).toFixed(0)}%`);
    check('ct', 'life expectancy at 2100 ≥ 40 (decline, not die-off)', at(s, 'le', 2100) >= 40, at(s, 'le', 2100).toFixed(0));
  }
  { // popstab — population frozen, capital unbound -> resource crisis
    const s = R.popstab;
    const range = peak(s, 'pop', 1980, 2050).val / trough(s, 'pop', 1980, 2050).val;
    check('popstab', 'population held ~constant after 1975', range < 1.15, `max/min ${range.toFixed(2)}`);
    check('popstab', 'capital-diversion crisis: IOPC <20% of peak by 2100', at(s, 'iopc', 2100) < 0.2 * peak(s, 'iopc').val, at(s, 'iopc', 2100).toFixed(0));
  }
  { // capstab — capital frozen, population unbound -> famine
    const s = R.capstab, pp = peak(s, 'pop');
    check('capstab', 'population overshoots then falls ≥40%', at(s, 'pop', 2100) <= 0.6 * pp.val, `${(100 * at(s, 'pop', 2100) / pp.val).toFixed(0)}% of peak`);
    check('capstab', 'industrial output per capita collapses (<15% of peak)', at(s, 'iopc', 2100) < 0.15 * peak(s, 'iopc').val, at(s, 'iopc', 2100).toFixed(0));
  }
  { // popcap — the missed 1975 equilibrium
    const s = R.popcap;
    check('popcap', 'stable population (2100 within 10% of 2000)', Math.abs(at(s, 'pop', 2100) / at(s, 'pop', 2000) - 1) < 0.1, `${(at(s, 'pop', 2100) / 1e9).toFixed(1)}bn`);
    check('popcap', 'high welfare: life expectancy ≥75 at 2100', at(s, 'le', 2100) >= 75, at(s, 'le', 2100).toFixed(0));
    check('popcap', 'pollution never exceeds 4x 1970', peak(s, 'ppolx').val < 4, `${peak(s, 'ppolx').val.toFixed(1)}x`);
    check('popcap', 'sustained industrial output per capita ≥350', at(s, 'iopc', 2100) >= 350, at(s, 'iopc', 2100).toFixed(0));
  }
  { // sw — deliberate equilibrium, post-2002
    const s = R.sw;
    check('sw', 'population stabilizes at 6–8.5bn', near(at(s, 'pop', 2100), 6e9, 8.5e9), `${(at(s, 'pop', 2100) / 1e9).toFixed(1)}bn`);
    check('sw', 'high welfare: life expectancy ≥75 through 2100', at(s, 'le', 2100) >= 75, at(s, 'le', 2100).toFixed(0));
    check('sw', 'pollution stays below 6x 1970', peak(s, 'ppolx').val < 6, `${peak(s, 'ppolx').val.toFixed(1)}x`);
    check('sw', 'food per capita sustained ≥450', at(s, 'fpc', 2100) >= 450, at(s, 'fpc', 2100).toFixed(0));
  }
  { // delayed — dip, then degraded equilibrium
    const s = R.delayed, ipPre = peak(s, 'iopc', 1900, 2045);
    const fpMin = trough(s, 'fpc', 2030, 2075), fpRec = peak(s, 'fpc', fpMin.year, 2100);
    check('delayed', 'welfare contraction: IOPC falls ≥30% from pre-2045 peak', trough(s, 'iopc', ipPre.year, 2100).val <= 0.7 * ipPre.val, `${(100 * trough(s, 'iopc', ipPre.year, 2100).val / ipPre.val).toFixed(0)}%`);
    check('delayed', 'dip then recover: food regains ≥10% from its trough', fpRec.val >= 1.1 * fpMin.val, `${fpMin.val.toFixed(0)} -> ${fpRec.val.toFixed(0)}`);
    check('delayed', 'degraded vs SW: lower end-century IOPC', at(s, 'iopc', 2100) < at(R.sw, 'iopc', 2100), `${at(s, 'iopc', 2100).toFixed(0)} vs ${at(R.sw, 'iopc', 2100).toFixed(0)}`);
    check('delayed', 'stabilized: population no longer collapsing (2100 ≥ 85% of peak)', at(s, 'pop', 2100) >= 0.85 * peak(s, 'pop').val, `${(100 * at(s, 'pop', 2100) / peak(s, 'pop').val).toFixed(0)}%`);
  }

  const failed = checks.filter(c => !c.pass);
  return { checks, passed: checks.length - failed.length, failed: failed.length };
}
